import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, Mail, MapPin, ChevronDown, Search, LogIn, LogOut, LayoutDashboard, User as UserIcon, ShoppingBag } from 'lucide-react';
import { Button } from './ui/button';
import { COMPANY, LOGO_URL } from '../mock';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SettingsContext';
import { useCart } from '../context/CartContext';

const NAV_FULL = [
  { to: '/', label: 'Home' },
  { to: '/inventory', label: 'Inventory', flag: 'publish_inventory' },
  { to: '/parts', label: 'Parts & Accessories', flag: 'publish_parts' },
  { to: '/memberships', label: 'Memberships', flag: 'subscriptions_visible' },
  { to: '/services', label: 'Services' },
  { to: '/notices', label: 'Notices', flag: 'publish_notices' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' }
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings();
  const { count: cartCount } = useCart() || { count: 0 };
  const published = !!settings?.public_site_published;
  const partsVisible = !!settings?.publish_parts;
  const isAdmin = user?.role === 'admin';
  const NAV = NAV_FULL.filter(n => {
    if (!published && !isAdmin) return false; // when site unpublished, hide all public nav from public users
    if (n.flag && settings && settings[n.flag] === false && !isAdmin) return false;
    return true;
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-black text-white text-[12px] tracking-wide">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between h-9">
          <div className="hidden md:flex items-center gap-6 text-white/80">
            <span className="inline-flex items-center gap-1.5"><MapPin size={13} className="text-[#e11d2c]" /> {COMPANY.address}</span>
            <span className="hidden lg:inline-flex items-center gap-1.5"><Mail size={13} className="text-[#e11d2c]" /> {COMPANY.email}</span>
          </div>
          <div className="flex items-center gap-5 text-white/90">
            <span className="inline-flex items-center gap-1.5"><span className="pulse-dot" /> Live inventory</span>
            <a href={`tel:${COMPANY.phone}`} className="inline-flex items-center gap-1.5 hover:text-[#e11d2c] transition-colors">
              <Phone size={13} /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      <div className={`bg-white border-b transition-all ${scrolled ? 'border-black/10 shadow-[0_6px_24px_-12px_rgba(0,0,0,0.12)]' : 'border-transparent'}`}>
        <div className="max-w-7xl mx-auto px-5 lg:px-8 h-[78px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-14 h-14 rounded-xl overflow-hidden bg-black ring-1 ring-black/10 shadow-sm">
              <img src={LOGO_URL} alt="Timeless Auto Imports" className="w-full h-full object-cover" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-[19px] font-bold tracking-tight text-black">Timeless <span className="text-[#e11d2c]">Auto</span> Imports</div>
              <div className="text-[10px] tracking-[0.16em] font-semibold text-black/55 uppercase mt-0.5">Don’t Just Dream it, Drive it</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                className={({isActive}) => `relative px-4 py-2 text-[14px] font-medium tracking-wide transition-colors ${isActive ? 'text-[#e11d2c]' : 'text-black/80 hover:text-black'}`}
              >
                {({isActive}) => (
                  <span>
                    {n.label}
                    <span className={`absolute left-4 right-4 -bottom-0.5 h-[2px] bg-[#e11d2c] origin-left transition-transform duration-300 ${isActive ? 'scale-x-100' : 'scale-x-0'}`} />
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/inventory')}
              aria-label="Search inventory"
              className="hidden md:grid place-items-center w-10 h-10 rounded-full border border-black/15 hover:border-[#e11d2c] hover:text-[#e11d2c] transition-colors"
            >
              <Search size={16} />
            </button>

            {(partsVisible || isAdmin) && (published || isAdmin) && (
              <button
                onClick={() => navigate('/cart')}
                aria-label="View cart"
                data-testid="header-cart-btn"
                className="relative grid place-items-center w-10 h-10 rounded-full border border-black/15 hover:border-[#e11d2c] hover:text-[#e11d2c] transition-colors"
              >
                <ShoppingBag size={16} />
                {cartCount > 0 && (
                  <span
                    data-testid="header-cart-badge"
                    className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#e11d2c] text-white text-[10px] font-bold grid place-items-center border-2 border-white"
                  >
                    {cartCount > 99 ? '99+' : cartCount}
                  </span>
                )}
              </button>
            )}

            {user ? (
              <div className="hidden md:flex items-center gap-2">
                {user.role === 'admin' && (
                  <Button onClick={() => navigate('/admin')} variant="outline" className="rounded-full h-10 px-4 border-black/15 hover:border-black hover:bg-black hover:text-white text-[13px] font-semibold">
                    <LayoutDashboard size={14} className="mr-1.5" /> Admin
                  </Button>
                )}
                <button onClick={() => navigate('/account')} data-testid="header-account-btn" className="w-10 h-10 grid place-items-center rounded-full border border-black/15 hover:border-[#e11d2c] hover:text-[#e11d2c] transition-colors overflow-hidden" title="My Account">
                  {user.picture ? <img src={user.picture} alt="u" className="w-full h-full rounded-full object-cover" /> : <UserIcon size={15} />}
                </button>
              </div>
            ) : published ? (
              <Button onClick={() => navigate('/login')} variant="outline" data-testid="header-signin-btn" className="hidden md:inline-flex rounded-full h-10 px-4 border-black/15 hover:border-black hover:bg-black hover:text-white text-[13px] font-semibold">
                <LogIn size={14} className="mr-1.5" /> Sign In
              </Button>
            ) : (
              <Button onClick={() => navigate('/login')} variant="outline" className="hidden md:inline-flex rounded-full h-10 px-4 border-black/15 hover:border-black hover:bg-black hover:text-white text-[13px] font-semibold">
                <LogIn size={14} className="mr-1.5" /> Staff Sign-in
              </Button>
            )}

            <Button
              onClick={() => navigate('/inventory')}
              className={`${(published || isAdmin) ? 'hidden md:inline-flex' : 'hidden'} btn-primary bg-[#e11d2c] hover:bg-[#c01526] text-white font-semibold tracking-wide rounded-full px-6 h-10`}
            >
              Browse Cars
            </Button>
            <button onClick={() => setOpen(true)} className="lg:hidden w-10 h-10 grid place-items-center rounded-full border border-black/15 hover:border-[#e11d2c] transition-colors" aria-label="Open menu">
              <Menu size={18} />
            </button>
          </div>
        </div>
      </div>

      <div className={`fixed inset-0 z-[60] lg:hidden transition-opacity duration-300 ${open ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}>
        <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
        <div className={`absolute right-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex items-center justify-between p-5 border-b border-black/10">
            <span className="font-display text-lg font-bold">Menu</span>
            <button onClick={() => setOpen(false)} className="w-10 h-10 grid place-items-center rounded-full hover:bg-black/5"><X size={18} /></button>
          </div>
          <div className="p-5 flex flex-col gap-1">
            {NAV.map(n => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === '/'}
                onClick={() => setOpen(false)}
                className={({isActive}) => `flex items-center justify-between px-4 py-4 rounded-lg text-[15px] font-medium ${isActive ? 'bg-[#e11d2c] text-white' : 'text-black hover:bg-black/5'}`}
              >
                {n.label}
                <ChevronDown className="-rotate-90" size={16} />
              </NavLink>
            ))}
            {user?.role === 'admin' && (
              <button onClick={() => { setOpen(false); navigate('/admin'); }} className="flex items-center justify-between px-4 py-4 rounded-lg text-[15px] font-medium bg-black text-white">
                Admin Dashboard <LayoutDashboard size={16} />
              </button>
            )}
            {user ? (
              <button onClick={() => { setOpen(false); navigate('/account'); }} data-testid="mobile-account-link" className="flex items-center justify-between px-4 py-4 rounded-lg text-[15px] font-medium text-black hover:bg-black/5">
                <span className="inline-flex items-center gap-2"><UserIcon size={16} className="text-[#e11d2c]" /> My Account</span>
                <ChevronDown className="-rotate-90" size={16} />
              </button>
            ) : published ? (
              <button onClick={() => { setOpen(false); navigate('/login'); }} data-testid="mobile-signin-link" className="flex items-center justify-between px-4 py-4 rounded-lg text-[15px] font-medium text-black hover:bg-black/5">
                <span className="inline-flex items-center gap-2"><LogIn size={16} className="text-[#e11d2c]" /> Sign In</span>
                <ChevronDown className="-rotate-90" size={16} />
              </button>
            ) : null}
            {(partsVisible || isAdmin) && (published || isAdmin) && (
              <button onClick={() => { setOpen(false); navigate('/cart'); }} data-testid="mobile-cart-link" className="flex items-center justify-between px-4 py-4 rounded-lg text-[15px] font-medium text-black hover:bg-black/5">
                <span className="inline-flex items-center gap-2"><ShoppingBag size={16} className="text-[#e11d2c]" /> Cart</span>
                {cartCount > 0 ? (
                  <span className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#e11d2c] text-white text-[11px] font-bold grid place-items-center">{cartCount > 99 ? '99+' : cartCount}</span>
                ) : (
                  <ChevronDown className="-rotate-90" size={16} />
                )}
              </button>
            )}
            <Button onClick={() => { setOpen(false); navigate('/inventory'); }} className="mt-4 bg-[#e11d2c] hover:bg-[#c01526] text-white h-12 rounded-full font-semibold">
              Browse Inventory
            </Button>
            {user && (
              <Button onClick={() => { setOpen(false); handleLogout(); }} variant="outline" className="mt-2 border-black/15 h-12 rounded-full font-semibold"><LogOut size={15} className="mr-2" /> Log out</Button>
            )}
            <a href={`tel:${COMPANY.phone}`} className="mt-2 flex items-center justify-center gap-2 h-12 rounded-full border border-black/15 text-black font-semibold">
              <Phone size={16} /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>
    </header>
  );
}
